import { Controller } from '@hotwired/stimulus'

// Generic tabs controller for simple tab navigation.
// Expected markup inside the controller element:
// - Tab buttons/links with `data-tabs-target="tab"` and `href="#panelId"`
// - Panels with `data-tabs-target="panel"` and `id="panelId"`
export default class extends Controller {
  static targets = ['tab', 'panel']
  static values = { defaultTab: String }

  connect() {
    // Activate from hash if present, else first panel
    const ids = this.panelTargets.map((p) => p.id).filter(Boolean)
    const hash = window.location.hash?.replace(/^#/, '')
    const preferred = this.hasDefaultTabValue ? this.defaultTabValue : null
    const initialId = ids.includes(hash)
      ? hash
      : preferred && ids.includes(preferred)
      ? preferred
      : ids[0]
    if (initialId) this.showById(initialId)

    // React to hash changes from external links/buttons
    this._onHashChange = () => {
      const id = window.location.hash?.replace(/^#/, '')
      if (!id) return
      if (this.panelTargets.some((p) => p.id === id)) this.showById(id)
    }
    window.addEventListener('hashchange', this._onHashChange)
  }

  switch(event) {
    event.preventDefault()
    const disabled = event.currentTarget.getAttribute('aria-disabled') === 'true'
    if (disabled) return
    const href = event.currentTarget.getAttribute('href') || ''
    const id = href.replace(/^#/, '')
    if (!id) return
    this.showById(id)
  }

  showById(id) {
    // Toggle panels
    this.panelTargets.forEach((panel) => {
      const active = panel.id === id
      panel.classList.toggle('hidden', !active)
      panel.classList.toggle('block', active)
      panel.setAttribute('aria-hidden', active ? 'false' : 'true')
      if (active) panel.setAttribute('tabindex', '0')
    })

    // Toggle active tab styling
    this.tabTargets.forEach((tab) => {
      const target = (tab.getAttribute('href') || '').replace(/^#/, '')
      const active = target === id

      // For traditional tabs
      tab.classList.toggle('tab-active', active)

      // For card-based navigation (border color switch)
      if (active) {
        tab.classList.remove('border-gray-200')
        tab.classList.add('border-yellow-400')
      } else {
        tab.classList.remove('border-yellow-400')
        tab.classList.add('border-gray-200')
      }

      tab.setAttribute('aria-selected', active ? 'true' : 'false')
      tab.setAttribute('tabindex', active ? '0' : '-1')
    })

    // Update hash without scrolling
    try {
      history.replaceState(null, '', `#${id}`)
    } catch (_) {}
  }

  onKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(event.key)) return
    event.preventDefault()

    const enabledTabs = this.tabTargets.filter(
      (t) => t.getAttribute('aria-disabled') !== 'true'
    )
    if (enabledTabs.length === 0) return

    const currentEl = event.target.closest('[data-tabs-target="tab"]')
    let index = enabledTabs.indexOf(currentEl)
    if (index === -1) index = 0

    let nextIndex = index
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + enabledTabs.length) % enabledTabs.length
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % enabledTabs.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = enabledTabs.length - 1

    const nextTab = enabledTabs[nextIndex]
    const id = (nextTab.getAttribute('href') || '').replace(/^#/, '')
    if (!id) return
    this.showById(id)
    nextTab.focus()
  }

  disconnect() {
    if (this._onHashChange) window.removeEventListener('hashchange', this._onHashChange)
  }
}
